import {
	ContextMenu,
	ContextMenuDeleteItem,
	ContextMenuItem,
	DrawerAutoDataList,
	FormattedDate,
	IBaseEntityResource,
	IDataListColumn,
	Icon,
	SingleSelectField,
	useCrudEntity,
	useEditDrawer,
} from "@dgs/core";
import { OwnVacationDto, VacationUpdateOwnDto } from "apiGen";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { vacationOwnService } from "~shared/api/vacation/vacation_service";
import { ALL_VACATION_STATUS, IVacationStatus } from "~shared/types";
import { OwnVacationCreateDrawer, OwnVacationUpdateDrawer } from "./OwnVacationDrawer";

const CheckIcon = styled(Icon)`
	width: 24px;
	height: 24px;
`;

export const OwnVacationList = () => {
	const { t } = useTranslation();
	const crudOperations = useCrudEntity<VacationUpdateOwnDto, IBaseEntityResource>(vacationOwnService, {
		entityName: t("Vacation"),
		messages: {
			create: t("Vacation was saved."),
			update: t("Vacation was saved."),
			delete: t("Vacation was deleted."),
		},
	});
	const editDrawerProps = useEditDrawer();

	const dataListColumns: IDataListColumn<OwnVacationDto>[] = useMemo(
		() => [
			{
				heading: t("Start"),
				valueKey: "start",
				type: "value",
				render: ({ start }) => <FormattedDate date={start} />,
			},
			{
				heading: t("End"),
				valueKey: "end",
				type: "value",
				render: ({ end }) => <FormattedDate date={end} />,
			},
			{
				heading: t("Days"),
				valueKey: "days",
				type: "value",
			},
			{
				heading: t("Remark"),
				valueKey: "remark",
				type: "value",
			},
			{
				heading: t("Decline reason"),
				valueKey: "declineReason",
				type: "value",
			},
			{
				heading: t("Status"),
				valueKey: "status",
				type: "value",
				render: ({ status }) => t(status),
				filter: {
					operators: ["="],
					renderValueField: ({ fieldName }) => (
						<SingleSelectField options={[...ALL_VACATION_STATUS]} label={t("Status")} name={fieldName} getLabel={t} />
					),
					renderActiveColumnFilterValue: (value: IVacationStatus) => <span>{t(value)}</span>,
				},
			},
			{
				heading: t("Half day"),
				valueKey: "halfDay",
				type: "value",
				render: ({ halfDay }) => (halfDay ? <CheckIcon $icon="check" /> : ""),
			},
			{
				heading: t("Stand in"),
				valueKey: "standIn.id",
				render: ({ standIn }) => (standIn ? `${standIn.firstName} ${standIn.lastName}` : null),
				type: "value",
			},
			{
				heading: "",
				type: "actions",
				size: "max-content",
				action: ({ id, status }) =>
					status === "pending" ? (
						<ContextMenu displayShortcutsAs="icon">
							<ContextMenuItem
								title={t("Edit vacation")}
								label={t("Edit vacation")}
								action={() => editDrawerProps.handleOpen(id)}
								icon="edit"
								shortcut="primary"
							/>
							<ContextMenuDeleteItem
								action={() => crudOperations.deleteEntity(id)}
								title={t("Delete vacation")}
								label={t("Delete vacation")}
								heading={t("Delete")}
								labels={{ close: t("Close"), confirm: t("Confirm") }}
							>
								{t("Are you sure you want to delete this vacation?")}
							</ContextMenuDeleteItem>
						</ContextMenu>
					) : null,
			},
		],
		[crudOperations, editDrawerProps, t],
	);

	return (
		<>
			<DrawerAutoDataList
				canCreate
				onRowClick={({ id, status }) => {
					if (status === "pending") {
						return editDrawerProps.handleOpen(id);
					}
				}}
				columns={dataListColumns}
				service={vacationOwnService}
				renderCreateDrawer={(drawerProps) => (
					<OwnVacationCreateDrawer
						{...drawerProps}
						onSubmit={({ standIn, ...formState }) => {
							return crudOperations.createEntity({
								...formState,
								standInId: standIn?.id ?? null,
							});
						}}
					/>
				)}
				translations={{
					title: t("My vacations"),
					empty: t("No vacation available."),
				}}
			/>
			{editDrawerProps.editEntityId && (
				<OwnVacationUpdateDrawer
					id={editDrawerProps.editEntityId}
					open={editDrawerProps.open}
					onClose={editDrawerProps.onClose}
					onClosed={editDrawerProps.onClosed}
					onSubmit={({ standIn, ...formState }) => {
						return crudOperations.updateEntity(editDrawerProps.editEntityId as number, {
							...formState,
							standInId: standIn?.id ?? null,
						});
					}}
				/>
			)}
		</>
	);
};
